import { Box, Card, CardContent, Typography } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { useQuery } from '@tanstack/react-query';
import { authLogin, AuthStatus, checkProviderInviteLink } from '@uplift/api';
import { useFeatureFlag, useIsMobile, useL10n, useStore } from '@uplift/hooks';
import { Button, CheckboxWithLabel, Loader, PasswordField, TextInput } from '@uplift/ui-v2';
import { Formik } from 'formik';
import { InviteForm, getInviteFormValidation } from './validation';
import { Redirect, useHistory } from 'react-router';
import { ROUTING_CONFIG, onboardingError } from '@uplift/constants';
import { useCallback, useMemo } from 'react';
import { StickyButton } from '../../components';
import { useSetPasswordMutation } from '@uplift/queries';

export type WelcomePageProps = {
  authUrl: string;
};

export const WelcomePage: React.VFC<WelcomePageProps> = ({ authUrl }) => {
  const { classes } = useStyles();
  const history = useHistory();
  const isMobile = useIsMobile('sm');
  const l10n = useL10n();
  const { authStore } = useStore();
  const setPasswordMutation = useSetPasswordMutation();
  const salesforceCredentialingEnabled = useFeatureFlag('SALESFORCE_CREDENTIALING_ENABLED') === true;

  const params = new URLSearchParams(window.location.search);
  const inviteToken = params.get('invite_token');

  const { data, isLoading } = useQuery({
    queryKey: [ 'provider-invite', inviteToken ],
    queryFn: () => checkProviderInviteLink({ invite_token: inviteToken }),
    enabled: !!inviteToken
  });

  const handleSubmit = useCallback(async (values: InviteForm, { setErrors }) => {
    const username = values.email;
    const password = values.password;

    try {
      await setPasswordMutation.mutateAsync({
        token: inviteToken,
        password: values.password,
        isInviteToken: true,
        ...(values.onboardingTermsAccepted && { onboardingTermsAccepted: true }),
      });

      const loginRes = await authLogin({ username, password });

      if ('error' in loginRes) {
        setErrors(loginRes.error);
        return;
      }

      if (loginRes.status !== AuthStatus.REDIRECT) {
        setErrors('MFA improperly set up');
        return;
      }

      authStore.setTokens({
        accessToken: loginRes.refreshToken,
        refreshToken: loginRes.refreshToken
      });

      authStore.setTokensInLocalStorage();

      history.push(ROUTING_CONFIG.onboardingGeneral);
    } catch (err) {
      setErrors({ error: err.message });
    }
  }, [ authStore, history, inviteToken ]);

  const { initialValues, validation } = useMemo(() => ({
    initialValues: {
      email: data?.user?.email ?? '',
      password: '',
      onboardingTermsAccepted: false
    },
    validation: getInviteFormValidation(l10n, salesforceCredentialingEnabled)
  }), [ data?.user?.email, l10n, salesforceCredentialingEnabled ]);

  if (!inviteToken) {
    return <Redirect to={ROUTING_CONFIG.login} />;
  }

  if (isLoading) {
    return (
      <Box className={classes.loader}>
        <Loader />
      </Box>
    );
  }

  if (data?.error || !data?.user) {
    if (data?.error === 'account_is_created') {
      authStore.logout(authUrl, {
        redirectAfterLogin: ROUTING_CONFIG.onboardingGeneral,
      });

      return;
    }

    if (data?.error === 'invalid_token') {
      return <Redirect to={onboardingError.createLink({}, { error: 'invalid_invite' })} />;
    }

    return (
      <Box>
        {data?.error}
      </Box>
    );
  }

  return (
    <Box width="100%">
      <Formik
        initialValues={initialValues}
        onSubmit={handleSubmit}
        validationSchema={validation}
        isInitialValid={false}
      >
        {formik => (
          <Box display="flex" flexDirection="column" gap="16px">
            <Box px={{ xs: 1, sm: 0 }}>
              <Typography variant="h4" color="textPrimary">
                Set Password
              </Typography>
            </Box>
            <Card className={classes.card} square={isMobile} elevation={isMobile ? 0 : 1}>
              <CardContent className={classes.content}>
                <TextInput name="email" type="email" label="Email Address" disabled fullWidth />
                <PasswordField label="Password" name="password" stackValidatorsOnMobile />
              </CardContent>
            </Card>
            {salesforceCredentialingEnabled && (
              <Box px={{ xs: 2, sm: 0 }}>
                <CheckboxWithLabel
                  name="onboardingTermsAccepted"
                  color="secondary"
                  label={(
                    <Typography variant="body2" sx={{ lineHeight: '20px', letterSpacing: '0.15px', color: '#212121' }}>
                      I agree to UpLift Behavioral Health P.C.{'\u2019'}s Independent Contractor Agreement and any applicable State Attestations required to practice. In addition, I agree to receive calls, messages & emails from UpLift concerning my application, account and other important notifications. Message and data rates may apply. Message frequency varies.
                    </Typography>
                  )}
                />
              </Box>
            )}
            <StickyButton>
              <Button
                variant="contained"
                color="secondary"
                disabled={!formik.isValid || formik.isSubmitting}
                onClick={formik.handleSubmit}
              >
                Next
              </Button>
            </StickyButton>
          </Box>
        )}
      </Formik>
    </Box>
  );
};

const useStyles = makeStyles()(theme => ({
  card: {
    width: '100%',
    [theme.breakpoints.down('sm')]: {
      borderBottom: `1px solid ${theme.palette.divider}`,
      borderTop: `1px solid ${theme.palette.divider}`,
      boxShadow: 'none',
    }
  },
  content: {
    display: 'flex',
    flexDirection: 'column',
    gap: '16px'
  },
  loader: {
    display: 'grid',
    placeItems: 'center',
  }
}));
